import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Box,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Modal,
  Select,
  Toolbar,
  useMediaQuery,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
// import i18n from "../../i18n";
import theme from "../../theme";
import { getRGBAFromColor } from "../../utils/methods/getCssVariables";
import HeaderLogo from "../assets/header-logo.svg";
import ContactUsForm from "../custom/ContactUsForm";
import { PrimaryButton, SecondaryButton } from "../ui/button";
import LogInAs from "./components/LogInAs";

const Header = () => {
  const { t, i18n } = useTranslation("header");
  const isMobileOrTablet = useMediaQuery("(max-width:1300px)");
  const [openContactUsForm, setOpenContactUsForm] = useState(false);
  const [drawerFlag, setDrawerFlag] = useState(false);
  const [loginModalFlag, setLoginModalFlag] = useState(false);
  const [language, setLanguage] = useState(i18n.language === "en" ? "ENG" : "FR");

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    i18n.changeLanguage(newLanguage === "ENG" ? "en" : "fr");
  };

  const navItems = [
    { label: t("Home") },
    { label: t("How it works") },
    { label: t("Our Promise") },
    { label: t("Dealer Network") },
    { label: t("Blog") },
    { label: t("FAQs") },
  ];

  return (
    <Grid
      item
      sx={{
        width: "100%",
        alignItems: "center",
        background: `${theme.palette.background.paper}`,
        boxShadow: `10px 10px 10px ${getRGBAFromColor({
          alpha: "0.1",
          color: theme.palette.text.primary,
        })}`,
        borderBottom: "1px solid rgba(0,0,0,0.12)",
        borderRadius: 10,
        my: 2,
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          px: { xs: 1, sm: 2, md: 3 },
          gap: 2,
          pb: 2,
          pt: 2,
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            height: isMobileOrTablet
              ? "40px"
              : { md: "40px", lg: language === "FR" ? "22px" : "30px", xl: language === "FR" ? "35px" : "40px" },
            transition: "all 0.3s ease-in-out",
            flexShrink: 0,
          }}
        >
          <img
            src={HeaderLogo}
            alt='Car Chaser Logo'
            style={{
              height: "100%",
              width: "auto",
              objectFit: "contain",
            }}
          />
        </Box>

        {isMobileOrTablet ? (
          <>
            {/* Mobile Hamburger Icon */}
            <IconButton onClick={() => setDrawerFlag(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer anchor='right' open={drawerFlag} onClose={() => setDrawerFlag(false)}>
              <Box
                sx={{
                  width: 250,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
                  <IconButton onClick={() => setDrawerFlag(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                <List>
                  {navItems.map((item, index) => (
                    <ListItem button key={index}>
                      <ListItemText primary={item.label} />
                    </ListItem>
                  ))}
                  <ListItem>
                    <Select
                      value={language}
                      onChange={(e) => handleLanguageChange(e.target.value)}
                      fullWidth
                      sx={{
                        mt: 2,
                        color: theme.palette.text.primary,
                      }}
                    >
                      <MenuItem value='ENG'>ENG</MenuItem>
                      <MenuItem value='FR'>FR</MenuItem>
                    </Select>
                  </ListItem>
                  <ListItem>
                    <PrimaryButton
                      fullWidth
                      onClick={() => {
                        setOpenContactUsForm(true);
                        setDrawerFlag(false);
                      }}
                    >
                      Contact Us
                    </PrimaryButton>
                  </ListItem>
                  <ListItem>
                    <PrimaryButton
                      variant='outlined'
                      fullWidth
                      onClick={() => {
                        setLoginModalFlag((loginModalFlag) => !loginModalFlag);
                      }}
                      sx={{
                        mt: 1,
                        border: `1px solid ${theme.palette.primary.main}`,
                        background: `transparent !important`,
                        color: `${theme.palette.primary.main} !important`,
                      }}
                    >
                      Login
                    </PrimaryButton>
                  </ListItem>
                </List>
              </Box>
            </Drawer>
          </>
        ) : (
          <>
            {/* Desktop Navigation Links */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { lg: language === "FR" ? 0.1 : 0.5, xl: language === "FR" ? 1 : 2 },
                flexWrap: "wrap",
              }}
            >
              {navItems.map((item, index) => (
                <SecondaryButton
                  key={index}
                  variant='text'
                  sx={{
                    color: `${theme.palette.text.primary} !important`,
                    fontSize: {
                      lg: language === "FR" ? "0.75rem" : "0.875rem",
                      xl: language === "FR" ? "0.875rem" : "1rem",
                    },
                  }}
                >
                  {item.label}
                </SecondaryButton>
              ))}
            </Box>

            {/* Right Side Actions */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { lg: language === "FR" ? 0.5 : 1, xl: language === "FR" ? 1 : 2 },
                flexWrap: "wrap",
              }}
            >
              <Select
                value={language}
                onChange={(e) => handleLanguageChange(e.target.value)}
                sx={{
                  height: "2rem",
                  width: 80,
                  color: theme.palette.text.primary,
                  "& .MuiSelect-select": {
                    color: theme.palette.text.primary,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    },
                  },
                }}
              >
                <MenuItem value='ENG'>ENG</MenuItem>
                <MenuItem value='FR'>FR</MenuItem>
              </Select>

              <PrimaryButton
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  background: `${theme.palette.primary.main} !important`,
                  color: `${theme.palette.text.secondary} !important`,
                }}
                onClick={() => {
                  setOpenContactUsForm(true);
                }}
              >
                Contact Us
              </PrimaryButton>
              <PrimaryButton
                variant='outlined'
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  border: `1px solid ${theme.palette.primary.main}`,
                  background: `transparent !important`,
                  color: `${theme.palette.primary.main} !important`,
                }}
                onClick={() => {
                  setLoginModalFlag(true);
                }}
              >
                Login
              </PrimaryButton>
            </Box>
          </>
        )}
      </Toolbar>

      {/* Contact Us Modal */}
      {openContactUsForm && (
        <Modal
          open={true}
          onClose={() => setOpenContactUsForm(false)}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "90%",
              maxWidth: 600,
              bgcolor: "background.paper",
              p: 3,
              borderRadius: 2,
            }}
          >
            <ContactUsForm
              onSuccessCallback={() => {
                setOpenContactUsForm(false);
              }}
            />
          </Box>
        </Modal>
      )}

      {loginModalFlag ? (
        <Modal
          open={true}
          onClose={() => {
            setLoginModalFlag(false);
          }}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              maxWidth: "400px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <LogInAs
              handleClose={() => {
                setLoginModalFlag(false);
              }}
            />
          </Box>
        </Modal>
      ) : null}
    </Grid>
  );
};

export default Header;
